import React, { RefObject, useEffect, useState } from "react";
import { BsTelephone } from "react-icons/bs";
import { CiLocationOn } from "react-icons/ci";

import {
  RestaurantClosingRulesType,
  RestaurantOpeningHoursType,
} from "../../../../../api/types/restaurant.type";
import { translationsContent } from "../../../../../localization/translations";
import { useAppDispatch } from "../../../../../redux/hooks";
import { setRestaurantInfo } from "../../../../../redux/slices/restaurantSlice";
import { useLanguage } from "../../../../../store/lang.context";
import { useMap } from "../../../../../store/map.context";
import { translate } from "../../../../../utils/translate";
import Button from "../../../../ui/button/SimpleButton";
import { Position } from "../..";
import classes from "./item.module.scss";
import OpeningHours from "./OpeningHours/index";

interface Props {
  ID: number;
  LAT: number;
  LNG: number;
  name: string;
  location: string | undefined;
  phone_number: string | undefined;
  slug: string;
  available: boolean;
  isClosed?: boolean;
  isActive?: boolean;
  opening_hours: RestaurantOpeningHoursType[];
  closing_rules: RestaurantClosingRulesType[];
  setModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpen?: () => void;
  position: Position;
  inputRef: RefObject<HTMLInputElement>;
}

const RestaurantItem = ({
  ID,
  LAT,
  LNG,
  name,
  location,
  phone_number,
  opening_hours,
  closing_rules,
  position,
  inputRef,
  setModalIsOpen,
  setIsOpen,
  isClosed,
  isActive,
}: Props) => {
  const { map, setMap } = useMap();
  const [caretUp, setCaretUp] = useState(false);
  const [distance, setDistance] = useState("");

  const { language } = useLanguage();

  const dispatch = useAppDispatch();

  const getName = (value: string) => {
    return value.substring(10);
  };

  const onClickHandler = () => {
    setMap({
      id: ID,
      lat: LAT,
      lng: LNG,
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    inputRef.current!.value = location + " " + getName(name);
  };

  const setRestaurant = (id: number, name: string, address: string) => {
    dispatch(
      setRestaurantInfo({
        id,
        name,
        address,
        openingHours: opening_hours,
        closingRules: closing_rules,
      })
    );
    if (setModalIsOpen) setModalIsOpen(false);
    if (setIsOpen) setIsOpen();
  };

  useEffect(() => {
    if (caretUp)
      window.addEventListener("click", (e) => {
        if (
          !document
            .getElementById("openingHoursButton+" + ID.toString())
            ?.contains(e.target as Node)
        ) {
          setCaretUp(false);
        }
      });
  }, [caretUp, setCaretUp, ID]);

  useEffect(() => {
    if (position.lat !== 0 && position.lng !== 0) {
      const lat1 = (LAT * Math.PI) / 180;
      const lon1 = (LNG * Math.PI) / 180;
      const lat2 = (position.lat * Math.PI) / 180;
      const lon2 = (position.lng * Math.PI) / 180;
      const dlon = lon2 - lon1;
      const dlat = lat2 - lat1;
      const a =
        Math.pow(Math.sin(dlat / 2), 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin(dlon / 2), 2);

      const c = 2 * Math.asin(Math.sqrt(a));
      const r = 6371;

      setDistance(Math.ceil(c * r).toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  return (
    <div
      className={`${classes.container} ${map.id === ID && classes.clicked}`}
      onClick={() => {
        onClickHandler();
      }}
      id={ID.toString()}
    >
      <div className={classes.infoContainer}>
        <div className={classes.item}>
          <p className={classes.restaurantName}>{getName(name)}</p>
          {distance !== "" && <p>&nbsp;({`${distance} KM`})</p>}
        </div>
        <div className={classes.item}>
          <CiLocationOn />
          &nbsp; &nbsp;
          {location}
        </div>
        <div className={classes.item}>
          <BsTelephone style={{ width: "25px", height: "20px" }} />
          &nbsp; &nbsp;
          {phone_number}
        </div>
        <div
          className={classes.openingHours}
          onClick={() => {
            setCaretUp(!caretUp);
          }}
          id={`openingHoursButton+${ID.toString()}`}
        >
          {translate(translationsContent, "openUntil", language)}
          &nbsp;&nbsp;&nbsp;
          <span className={`${classes.caret} ${caretUp && classes.caretUp}`} />
        </div>
        {opening_hours && caretUp && (
          <div style={{ position: "relative" }}>
            <OpeningHours
              openingHours={opening_hours}
              closingRules={closing_rules}
            />
          </div>
        )}
      </div>
      {isActive && (
        <div className={`${classes.buttonContainer}`}>
          <Button
            label={`${translate(translationsContent, "orderNow", language)}`}
            onClick={() => setRestaurant(ID, name, `${location}`)}
            disabled={isClosed}
          />
        </div>
      )}
    </div>
  );
};

export default RestaurantItem;
